import { Component, computed, inject, input, Input, OnInit } from '@angular/core';
import { EmailAttachment, EmailMergeDto, EmailMessageDto, UserEmailDto } from '@artemis-software/wr-api';
import { createForm } from '@sonofabit/ng-core';
import { EmailValidationService } from '@services/email-validation.service';
import { CurrentUserService } from '@services/current-user.service';
import { RecipientSuggestion } from '@components/select-recipient/select-recipient.component';

@Component({
  standalone: false,
  selector: 'wr-send-email',
  templateUrl: './send-email.component.html',
  styleUrls: ['./send-email.component.scss']
})
export class  SendEmailComponent implements OnInit{
  @Input()
  title = '';
  @Input()
  message = '';
  @Input()
  emailMessage: EmailMessageDto = {
    subject: '',
    attachments: [],
    htmlBody: ''
  };
  @Input()
  recipients: string[]= [];

  @Input()
  cc: string[] = [];

  @Input()
  recipientSuggestions: RecipientSuggestion[] = [];

  @Input()
  allowAttachments = true;

  sendCopyToSender = input<boolean>(true);

  readonly currentUserService = inject(CurrentUserService);
  private readonly emailValidationService = inject(EmailValidationService);
  readonly currentUserEmail = computed(() => {
    const user = this.currentUserService.currentUser();
    if (user) {
      return user.email;
    }
    return undefined;
  });

  form?: ReturnType<typeof this.initForm>;

  ngOnInit() {
    this.form = this.initForm();
  }

  initForm() {
    return createForm(({ field  }) => {
      return {
        subject: field({
          value: this.emailMessage.subject,
        }),
        recipients: field({
          value: this.recipients ?? []
        }),
        cc: field({
          value: this.currentUserEmail() ? [this.currentUserEmail()!] : [],
        }),
        attachments: field({
          value: this.emailMessage.attachments,
        }),
        userAttachments: field({
          value: [] as EmailAttachment[],
        }),
        body: field({
          value: this.emailMessage.htmlBody,
        }),
        sendCopyToSender: field({
          value: true,
        })
      };
    });
  }

  submitResponse(): EmailMergeDto {
    if (!this.form) {
      throw new Error('Form is not initialized');
    }

    const subject = this.form.controls.subject.value || '';
    const body = this.form.controls.body.value || '';
    const recipientsControlValue = this.form.controls.recipients.value;
    const recipients = Array.isArray(recipientsControlValue) ? recipientsControlValue : [];
    const ccControlValue = this.form.controls.cc.value;
    const cc = Array.isArray(ccControlValue) ? ccControlValue : [];
    const sendCopyToSender = this.form.controls.sendCopyToSender.value;

    if (!recipients.length) {
      throw new Error('Es wurde kein Empfänger angegeben');
    }

    let attachments = this.form.controls.attachments.value || [];
    const userAttachments = this.form.controls.userAttachments.value || [];

    type AttachmentWithUrl = EmailAttachment & { presignedUrl: string };

    attachments = attachments.concat(
      userAttachments.map((attachment: EmailAttachment) => {
        const attachmentDetail = attachment as AttachmentWithUrl;
        return {
          attachmentUrl: attachmentDetail.presignedUrl,
          fileEnding: attachmentDetail.fileEnding,
          fileName: attachmentDetail.fileName,
          created: attachmentDetail.created || new Date().toISOString(),
          lastUpdated: attachmentDetail.lastUpdated || new Date().toISOString(),
          deleted: attachmentDetail.deleted,
        } as EmailAttachment;
      })
    );

    return {
      subject: subject,
      htmlBody: body,
      attachments: attachments,
      recipients: recipients,
      cc: cc,
      sendCopyToSender: sendCopyToSender,
    } as EmailMergeDto;
  }


  isFormValid(): boolean {
    if (!this.form) {
      return false;
    }

    const recipientsControlValue = this.form.controls.recipients.value;
    const recipients = Array.isArray(recipientsControlValue) ? recipientsControlValue : [];

    const ccControlValue = this.form.controls.cc.value;
    const cc = Array.isArray(ccControlValue) ? ccControlValue : [];

    if (!recipients.length) {
      return false;
    }

    const allRecipientsValid = recipients.every(emailAddress =>
      this.emailValidationService.isValidEmail(emailAddress)
    );
    const allCcValid = cc.every(emailAddress =>
      this.emailValidationService.isValidEmail(emailAddress)
    );

    return allRecipientsValid && allCcValid;
  }

  getUserProfile(user: UserEmailDto): string {
    const dummyUser = 'https://i.imgur.com/ujW6tKI.png';
    const profilePicture = user.userProfilePicture;
    if (profilePicture) {
      return profilePicture.presignedUrl ?? dummyUser;
    }
    return dummyUser;
  }
}
